import { useTranslate } from '@ur-solutions/react-hooks'
import type { MetricBand } from './metrics'
import { formatNumber, metricScore, weightPercent } from './metrics'

export interface GradeMetric extends MetricBand {
  key: string
  value: number
  score: number
  weight: number
  bestValue: number
  pointsToGain: number
}

/** Names, explanations and improvement tips for the grade's scored parts. */
export function useMetricTexts() {
  const translations = useTranslate({
    label: {
      idle_share: 'driver-grade.metric-idle_share',
      cruise_share: 'driver-grade.metric-cruise_share',
    },
    info: {
      idle_share: [
        'driver-grade.info-idle_share',
        { low: '', high: '', weight: '' },
      ],
      cruise_share: [
        'driver-grade.info-cruise_share',
        { high: '', zero: '', weight: '' },
      ],
    },
    tip: {
      idle_share: ['driver-grade.tip-idle_share', { value: '', best: '' }],
      cruise_share: ['driver-grade.tip-cruise_share', { value: '', best: '' }],
    },
    tipDone: ['driver-grade.tip-done', { metric: '' }],
  })

  const label = (key: string) =>
    translations.label[key as keyof typeof translations.label] ?? key

  const info = (metric: GradeMetric, metrics: GradeMetric[]) => {
    const weight = String(
      weightPercent(
        metric.weight,
        metrics.map(m => m.weight)
      )
    )
    if (metric.key === 'idle_share')
      return translations.info.idle_share({
        low: formatNumber(metric.bandLow, 0),
        high: formatNumber(metric.bandHigh, 0),
        weight,
      })
    if (metric.key === 'cruise_share')
      return translations.info.cruise_share({
        high: formatNumber(metric.bandHigh, 0),
        zero: formatNumber(metricScore(0, metric), 0),
        weight,
      })
    return ''
  }

  const tip = (metric: GradeMetric) => {
    if (metric.pointsToGain <= 0)
      return translations.tipDone({ metric: label(metric.key) })
    const tipFor = translations.tip[metric.key as keyof typeof translations.tip]
    if (!tipFor) return ''
    return tipFor({
      value: formatNumber(metric.value),
      best: formatNumber(metric.bestValue, 0),
    })
  }

  return { label, info, tip }
}
